import { type JSONData } from "../../base_classes/dto.d";
import { PackingSlipDto } from "./dto.gen";
import { type IPackingSlip } from "./entity.d";
import PackingSlipParsers from "./parsers";
import PackingSlipValidators from "./custom_validators";
import request from "@/data/api/request";
import { Exclude } from "class-transformer";
import { ValidationError } from "@/data/base_classes/validation_error";

export class PackingSlip extends PackingSlipDto implements IPackingSlip {
  constructor(
    pathVars?:
      | string
      | string[]
      | {
          id?: string;
        },
    data?: JSONData,
  ) {
    super(data, PackingSlipParsers, PackingSlipValidators);

    if (data !== undefined) {
      // Set data
      this.set(data);
    }

    this.buildPathObject(pathVars);
  }
  /// ///////////////////////////////////////
  /// //////// PRIVATE VARIABLES ////////////
  /// ///////////////////////////////////////

  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////

  get formattedAddress(): string {
    return (
      this.address.street +
      " " +
      this.address.houseNumber +
      ", " +
      this.address.postalCode +
      " " +
      this.address.city +
      ", " +
      this.address.country
    );
  }

  /// ///////////////////////////////////////
  /// ////////////// METHODS ////////////////
  /// ///////////////////////////////////////

  public async get(
    pathVars?:
      | string
      | string[]
      | {
          id?: string;
        }
      | undefined,
  ): Promise<PackingSlip> {
    return (await super.get(pathVars)) as PackingSlip;
  }

  @Exclude()
  async push(): Promise<void> {
    await request({
      url: `/packing_slip/${this.mainPackingSlipId || this.id}/push`,
      method: "post",
    });
  }

  @Exclude()
  async reset(): Promise<void> {
    await request({
      url: `/packing_slip/${this.mainPackingSlipId || this.id}/reset`,
      method: "post",
    });
  }

  @Exclude()
  async print(): Promise<void> {
    await request({
      url: `/packing_slip/${this.mainPackingSlipId || this.id}/print`,
      method: "post",
    });
  }
  @Exclude()
  async printLabel(): Promise<void> {
    await request({
      url: `/packing_slip/${this.mainPackingSlipId || this.id}/print-label`,
      method: "post",
    });
  }

  /// ///////////////////////////////////////
  /// /////////// STATIC METHODS ////////////
  /// ///////////////////////////////////////

  static override FromJSON(data?: JSONData): PackingSlip {
    return new PackingSlip(undefined, data);
  }

  static override FromJSONArray(data: JSONData[]): PackingSlip[] {
    return data.map((item) => PackingSlip.FromJSON(item));
  }

  static FromDto(dto: PackingSlipDto): PackingSlip {
    return new PackingSlip(undefined, dto.toJSON());
  }

  static async Reset(id: string): Promise<void> {
    return request({
      url: `/packing_slip/${id}/reset`,
      method: "post",
    });
  }

  static Print(id: string): Promise<void> {
    return request({
      url: `/packing_slip/${id}/print`,
      method: "post",
    });
  }

  static PrintLabel(id: string): Promise<void> {
    return request({
      url: `/packing_slip/${id}/print-label`,
      method: "post",
    });
  }

  static async SyncExternalPackingSlips(): Promise<void> {
    await request({
      url: "external/packing_slips",
      method: "GET",
    });
  }

  static async SyncExternalPackingSlip(slipNumber: string): Promise<void> {
    await request({
      url: "external/packing_slips",
      method: "GET",
      params: {
        slipNumber,
      },
    });
  }
}
